import React, { useEffect, useMemo, useState } from 'react';
import { Check, Eye, EyeOff, Loader2 } from 'lucide-react';
import { dataDictionaryApi } from './dataDictionaryApi';
import FolderPicker from './FolderPicker';
import TagPicker from './TagPicker';
import { cleanTags, errorMessage, formatNumber, normalizeList } from './dataDictionaryUtils';
import { dictionaryRowHasVendorTokens, vendorTokensIn } from '../../utils/signalProviders';

function Segmented({ value, onChange, options, label }) {
  return (
    <div className="flex rounded-lg bg-[#E8EEF5] p-[3px]" role="radiogroup" aria-label={label}>
      {options.map((opt) => {
        const on = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(opt.value)}
            className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-medium transition-colors ${
              on ? 'bg-white text-[#18181B] shadow-sm' : 'text-[#71717A] hover:text-[#18181B]'
            }`}
          >
            {opt.icon ? <opt.icon size={14} aria-hidden /> : null}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold tracking-[0.02em] text-[#71717A]">{label}</span>
      {children}
      {hint ? <div className="text-xs text-[#71717A]">{hint}</div> : null}
    </div>
  );
}

/**
 * Right-hand panel when several columns are checked. Only the fields the admin touches are sent.
 */
export default function BulkEditPanel({ rows, onClear, onApplied, folders, tagOptions }) {
  const [visibility, setVisibility] = useState('keep'); // keep | show | hide
  const [folderMode, setFolderMode] = useState('keep'); // keep | set | remove
  const [folderPath, setFolderPath] = useState([]);
  const [addTags, setAddTags] = useState([]);
  const [removeTags, setRemoveTags] = useState([]);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
  }, [rows.length]);

  const count = rows.length;

  const folderSummary = useMemo(() => {
    const counts = new Map();
    rows.forEach((r) => {
      const p = normalizeList(r.folder_list);
      const label = p.length ? p.join(' / ') : 'No folder';
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const visibilitySummary = useMemo(() => {
    const vis = rows.filter((r) => r.visible === true).length;
    return { visible: vis, hidden: rows.length - vis };
  }, [rows]);

  const existingTagOptions = useMemo(() => {
    const counts = new Map();
    rows.forEach((r) =>
      cleanTags(r.tags).forEach((t) => {
        const k = t.toLowerCase();
        const cur = counts.get(k) || { tag: t, count: 0 };
        cur.count += 1;
        counts.set(k, cur);
      })
    );
    return Array.from(counts.values()).sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
  }, [rows]);

  const changes = useMemo(() => {
    const c = {};
    if (visibility === 'show') c.visible = true;
    if (visibility === 'hide') c.visible = false;
    if (folderMode === 'set' && folderPath.length) c.folder_list = folderPath;
    if (folderMode === 'remove') c.folder_list = [];
    if (addTags.length) c.add_tags = addTags;
    if (removeTags.length) c.remove_tags = removeTags;
    return c;
  }, [visibility, folderMode, folderPath, addTags, removeTags]);
  const changeCount = Object.keys(changes).length;
  const vendorLint = useMemo(
    () =>
      rows.some(dictionaryRowHasVendorTokens) ||
      vendorTokensIn([folderPath.join('/'), addTags.join(',')]),
    [rows, folderPath, addTags]
  );

  const apply = async () => {
    if (!changeCount || applying) return;
    setApplying(true);
    setError('');
    try {
      const res = await dataDictionaryApi.bulkUpdate(rows, changes);
      onApplied?.(Array.isArray(res?.hits) ? res.hits : [], changes);
      setVisibility('keep');
      setFolderMode('keep');
      setFolderPath([]);
      setAddTags([]);
      setRemoveTags([]);
    } catch (e) {
      setError(errorMessage(e, 'Bulk update failed'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#F8FAFC]">
      <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-[#0062FB] bg-[#0062FB] text-white" aria-hidden>
            <Check size={11} strokeWidth={3} />
          </span>
          <span className="text-sm font-semibold text-[#18181B]">
            {formatNumber(count)} column{count === 1 ? '' : 's'} selected
          </span>
        </div>
        <button type="button" onClick={onClear} className="text-xs text-[#0062FB] hover:underline">
          Clear
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
        <p className="text-[13px] text-[#71717A]">Changes below apply to every selected column. Fields you leave untouched are kept as-is.</p>

        <Field
          label="Visibility"
          hint={
            <>
              Currently <b className="font-medium text-[#3F3F46]">{visibilitySummary.visible}</b> visible · <b className="font-medium text-[#3F3F46]">{visibilitySummary.hidden}</b> hidden
            </>
          }
        >
          <Segmented
            label="Visibility"
            value={visibility}
            onChange={setVisibility}
            options={[
              { value: 'keep', label: 'Keep' },
              { value: 'show', label: 'Show', icon: Eye },
              { value: 'hide', label: 'Hide', icon: EyeOff },
            ]}
          />
        </Field>

        <Field
          label="Folder"
          hint={
            <span className="block truncate">
              Currently in{' '}
              {folderSummary.slice(0, 3).map(([label, n], i) => (
                <React.Fragment key={label}>
                  {i > 0 ? ', ' : ''}
                  <b className="font-medium text-[#3F3F46]">{label}</b> ({n})
                </React.Fragment>
              ))}
              {folderSummary.length > 3 ? ` and ${folderSummary.length - 3} more` : ''}
            </span>
          }
        >
          <Segmented
            label="Folder action"
            value={folderMode}
            onChange={setFolderMode}
            options={[
              { value: 'keep', label: 'Keep' },
              { value: 'set', label: 'Move to…' },
              { value: 'remove', label: 'Remove' },
            ]}
          />
          {folderMode === 'set' ? (
            <FolderPicker value={folderPath} onChange={setFolderPath} folders={folders} placeholder="Choose a destination folder" />
          ) : null}
        </Field>

        <Field label="Add tags" hint="Added alongside each column's existing tags.">
          <TagPicker value={addTags} onChange={setAddTags} options={tagOptions} placeholder="Add tag to all…" />
        </Field>

        <Field label="Remove tags" hint={existingTagOptions.length ? 'Choose from tags present on the selected columns.' : 'Selected columns have no tags.'}>
          <TagPicker
            value={removeTags}
            onChange={setRemoveTags}
            options={existingTagOptions}
            placeholder="Remove tag from all…"
            allowCreate={false}
            disabled={existingTagOptions.length === 0}
          />
        </Field>

        <p className="border-t border-hairline pt-3 text-xs text-zinc-400">Display name and description are per-column and stay unchanged here.</p>

        {vendorLint ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" role="status">
            A selected column or this edit includes a data-vendor name. Segment Builder users will
            see it as Signal Provider ## — consider replacing the brand in display name, folder, or
            tags.
          </p>
        ) : null}

        {error ? (
          <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-hairline px-4 py-3">
        <span className="text-xs text-[#71717A]">
          {changeCount ? `${changeCount} change${changeCount === 1 ? '' : 's'} × ${formatNumber(count)} columns` : 'No changes yet'}
        </span>
        <button
          type="button"
          onClick={apply}
          disabled={!changeCount || applying}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#0062FB] px-3 text-[13px] font-medium text-white hover:bg-[#0052E0] disabled:opacity-50"
        >
          {applying ? <Loader2 size={13} className="animate-spin" aria-hidden /> : null}
          Apply to {formatNumber(count)} column{count === 1 ? '' : 's'}
        </button>
      </div>
    </div>
  );
}
